import { Box } from '@wix/design-system';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { errorsSlice } from '../../../common/reducers/errors';
import { IAttachmentError, IGeneralError } from '../../../common/reducers/types';
import { selectErrors, selectHasError } from '../../../common/selectors/errors';
import { selectFieldState } from '../../../common/selectors/field';
import { selectIsHidden } from '../FieldNext/selectors';
import { ErrorMessage } from './ErrorMessage';

type ErrorMessagesProps = {
  isAiEnabled: boolean;
};

type ErrorWithIsDeleted = (IGeneralError | IAttachmentError) & {
  isDeleted?: boolean;
};

export const ErrorMessages: FC<ErrorMessagesProps> = React.memo(({ isAiEnabled }) => {
  const dispatch = useDispatch();
  const hasError = useSelector(selectHasError);
  const errors = useSelector(selectErrors);
  const { nodeId } = useSelector(selectFieldState);
  const isFieldHidden = useSelector(selectIsHidden);
  const [nodeIdWithError, setNodeIdWithError] = useState<string>();
  const errorsPrevious = useRef<IGeneralError[] | IAttachmentError[]>([]);

  const clearErrors = useCallback(() => {
    dispatch(errorsSlice.actions.clearErrors());
  }, [dispatch]);

  useEffect(() => {
    if (hasError) {
      setNodeIdWithError(nodeId);
    } else if (nodeIdWithError) {
      setNodeIdWithError(undefined);
    }
  }, [hasError, nodeId, nodeIdWithError]);

  useEffect(() => {
    if (nodeIdWithError && nodeId !== nodeIdWithError) {
      clearErrors();
    }
  }, [nodeId, nodeIdWithError, clearErrors]);

  useEffect(() => {
    errorsPrevious.current = errors;
  }, [errors]);

  const mergedErrors = useMemo<ErrorWithIsDeleted[]>(() => {
    const existingErrors = errorsPrevious.current.map((error: IGeneralError | IAttachmentError) => ({
      ...error,
      isDeleted: !errors?.find((prevError) => prevError.localKey === error.localKey),
    }));

    const newErrors = errors.filter(
      (error: IGeneralError | IAttachmentError) =>
        !existingErrors.find(({ localKey }) => error.localKey === localKey),
    );

    return [...existingErrors, ...newErrors];
  }, [errors]);

  return (
    <Box
      position="absolute"
      bottom={isAiEnabled && !isFieldHidden ? 0 : '12px'}
      left="12px"
      right="12px"
      direction="vertical"
    >
      <Box width="720px" maxWidth="100%" margin="0 auto" direction="vertical" gap={2}>
        {mergedErrors.map((error, index) => (
          // use 'localKey' as key because it can not be duplicated
          <ErrorMessage show={!error.isDeleted} key={error.localKey} error={error} index={index} />
        ))}
      </Box>
    </Box>
  );
});
